import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function StandardTelephoniqueImmobilier() {
  return (
    <IntentLandingLayout
      canonical="/standard-telephonique-immobilier"
      title="Standard téléphonique immobilier IA | Immovox"
      description="Standard téléphonique IA pour agence immobilière : Immovox répond, qualifie vendeurs, acquéreurs et locataires, puis transmet le contexte à l'équipe."
      eyebrow="Standard téléphonique immobilier"
      h1="Un standard téléphonique immobilier qui comprend chaque appel."
      lede="Immovox répond lorsque votre agence ne peut pas décrocher, identifie vendeur, acquéreur, location ou gestion, puis prépare la reprise par votre équipe."
      serviceType="Standard téléphonique IA pour agence immobilière"
      primaryCtaLabel="Tester le standard"
      acquisitionHero={{
        visual: "standard",
        visualLabel: "Standard téléphonique immobilier",
        visualTitle: "Appel entrant · projet vendeur",
        visualText: "Le standard répond, comprend le motif et prépare la reprise par l'agence.",
        showPhoneDemo: true,
        strip: ["Décrocher quand l’équipe est indisponible", "Comprendre vente, achat, location ou gestion", "Transmettre une demande structurée"],
      }}
      definition={<>Un <strong>standard téléphonique immobilier IA</strong> est un accueil vocal capable de décrocher au nom de l'agence, comprendre si l'appel concerne une vente, un achat, une location ou une demande de gestion, puis restituer une synthèse structurée à l'équipe.</>}
      sections={[
        {
          title: "Pourquoi le standard est un point critique en immobilier",
          intro: "Le téléphone concentre des demandes très différentes au même moment, alors que les négociateurs et gestionnaires sont souvent hors du bureau.",
          bullets: [
            "Un vendeur peut appeler pendant une estimation ou une visite.",
            "Une annonce locative peut déclencher plusieurs demandes en peu de temps.",
            "Un acquéreur veut souvent vérifier un bien ou obtenir un rendez-vous immédiatement.",
            "Un locataire ou un propriétaire peut appeler pour une demande de gestion ou une urgence.",
            "Une messagerie classique enregistre un message, mais ne prépare pas le traitement de la demande.",
          ],
        },
        {
          title: "Comment fonctionne un standard téléphonique IA",
          intro: "Le principe est de conserver votre organisation téléphonique et d'ajouter une couche de traitement lorsque vous en avez besoin.",
          bullets: [
            "L'appel est renvoyé vers Immovox en débordement, hors horaires ou selon la règle choisie.",
            "L'assistant se présente au nom de l'agence et identifie le motif de l'appel.",
            "Il pose uniquement les questions utiles au scénario détecté.",
            "Il produit une synthèse avec le contexte, les coordonnées et les éléments collectés.",
            "Selon la configuration, la demande peut être transmise, signalée ou préparée pour un rappel.",
          ],
        },
        {
          title: "Vendeur, acquéreur, locataire : le standard ne pose pas les mêmes questions",
          intro: "Un standard utile ne déroule pas un script unique. La qualification dépend du motif de l'appel.",
          bullets: [
            "Vendeur : type de bien, localisation, projet d'estimation ou de vente, calendrier du projet.",
            "Acquéreur : bien concerné, budget, secteur recherché, critères et disponibilité.",
            "Candidat locataire : annonce ou bien concerné, situation, critères et coordonnées.",
            "Propriétaire bailleur : bien à gérer, besoin exprimé et délai.",
            "Demande de gestion : motif, niveau d'urgence, bien ou dossier concerné et meilleur canal de rappel.",
          ],
        },
        {
          title: "Débordement, hors horaires ou prise en charge continue",
          paragraphs: [
            "Une agence n'a pas besoin de remplacer son standard existant pour utiliser Immovox. Le renvoi d'appel permet d'activer le service uniquement après quelques sonneries, pendant les périodes de fermeture ou sur une ligne dédiée.",
            "Cette approche évite de modifier les habitudes de l'équipe : les appels restent traités par les collaborateurs quand ils sont disponibles, et l'IA prend le relais selon des règles explicites.",
          ],
        },
        {
          title: "Ce qu'un bon standard IA doit transmettre à l'équipe",
          bullets: [
            "Le motif exact de l'appel et le profil de l'appelant.",
            "Les coordonnées communiquées pendant l'échange.",
            "Le bien, l'annonce ou le dossier concerné lorsqu'il est identifiable.",
            "Les critères métier nécessaires pour préparer le rappel.",
            "Le contexte et le niveau d'urgence, sans inventer d'information absente de l'appel.",
          ],
        },
      ]}
      comparison={{
        title: "Standard classique ou standard téléphonique IA ?",
        intro: "Les deux peuvent coexister. La différence se situe surtout dans ce qui se passe quand personne n'est disponible pour décrocher.",
        headers: ["Besoin", "Standard classique", "Immovox"],
        rows: [
          ["Décrocher quand l'équipe est indisponible", "Messagerie ou renvoi simple", "Réponse vocale et qualification"],
          ["Comprendre le motif", "Dépend de l'interlocuteur disponible", "Questions adaptées au scénario"],
          ["Préparer le rappel", "Message libre", "Synthèse structurée"],
          ["Débordement et hors horaires", "Selon la configuration téléphonique", "Activation par renvoi d'appel"],
          ["Règles métier", "Principalement routage", "Scénarios configurables par type de demande"],
        ],
      }}
      faq={[
        { question: "Faut-il changer le numéro de l'agence ?", answer: "Non. Immovox peut être utilisé derrière un renvoi d'appel depuis votre numéro actuel. L'agence conserve son numéro public." },
        { question: "Le standard IA répond-il uniquement hors horaires ?", answer: "Non. Il peut être utilisé hors horaires, en débordement après quelques sonneries ou selon une organisation plus continue." },
        { question: "Peut-il distinguer un vendeur d'un acquéreur ?", answer: "Oui. La qualification est organisée par scénarios afin de collecter des informations différentes selon le motif de l'appel." },
        { question: "L'IA répond-elle à toutes les questions métier ?", answer: "Le périmètre dépend de la configuration. L'objectif principal est d'accueillir, qualifier et transmettre sans improviser une réponse métier non validée." },
        { question: "Que reçoit l'agence après l'appel ?", answer: "Une synthèse exploitable avec le motif, le contexte et les informations collectées pendant l'échange." },
      ]}
      related={[
        { to: "/permanence-telephonique-immobilier", label: "Permanence téléphonique immobilier", description: "Organiser le relais en débordement et hors horaires." },
        { to: "/qualification-appels-immobilier", label: "Qualification des appels", description: "Les informations à collecter selon chaque type de prospect." },
        { to: "/appels-manques-agence-immobiliere", label: "Appels manqués en agence", description: "Mesurer les appels perdus et structurer la réponse." },
      ]}
      ctaTitle="Tester un standard téléphonique immobilier IA"
      ctaText="Configurez vos scénarios, testez les échanges et choisissez ensuite quand Immovox doit prendre le relais."
    />
  );
}
